// ONE DECLARATION OF THE NAVIGATION (2026-09-04).
//
// Popty's navigation used to be derived in three places that had nothing
// comparing them: the primary tabs and sub-tab row in AppNavbar.vue, the hub
// cards in Admin.vue, and a hand-written `isAdminSection` computed that decided
// which primary tab lit up. Three defects in one hour on 2026-09-03 were all
// the same shape — two things that must agree, kept in step by convention:
//   * the admin sub-tab row and the admin hub cards listed different pages;
//   * `/builds` was in the sub-tab row but not in `isAdminSection`, so landing
//     there un-highlighted Admin and the whole row vanished underneath you.
//
// So: sections, their routes and their destinations are declared HERE, once,
// and every surface is derived from this file. Adding a page is one entry and
// cannot be half-done. `navigation.guard.test.js` fails if a route exists that
// this file does not account for, or if the two rendered surfaces could
// disagree, which is what stops this decaying back into convention.
//
// This module is plain JS on purpose — no Vue, no router import — so the guard
// test can read it directly.

// ---------------------------------------------------------------------------
// Matchers. A pattern is one of:
//   '/insights'   exact path
//   '/admin*'     path prefix ('/admin' itself included)
//   '@RouteName'  route name
// ---------------------------------------------------------------------------
export function matches(pattern, route) {
  if (!pattern) return false
  if (pattern.startsWith('@')) return route.name === pattern.slice(1)
  if (pattern.endsWith('*')) {
    const head = pattern.slice(0, -1)
    return route.path === head.replace(/\/$/, '') || route.path.startsWith(head)
  }
  return route.path === pattern
}

export function matchesAny(patterns, route) {
  return (patterns || []).some((p) => matches(p, route))
}

// ---------------------------------------------------------------------------
// PRIMARY TABS — the persistent top row, visible everywhere.
// A tab is active when any section that declares it as its primary is active,
// which is what makes '/builds' (an admin destination) light up Admin.
// ---------------------------------------------------------------------------
// `description` — ONE LINE saying what that destination actually does, drafted
// from what the page renders. It is not decoration: the explainer compiler
// (tools/explainer/compile.mjs) builds the app's own self-explanation out of
// these, so the explanation cannot disagree with the nav — there is no second
// copy to keep in step. An entry without one fails the compile. Admin items
// already carry theirs as `hub.description` (the card copy); `descriptionOf`
// reads either, so nothing is written twice.
export const PRIMARY_TABS = [
  { id: 'courses', label: 'Courses', to: '/courses', description: 'The course library, the canonical browsers, and every working surface inside a course.' },
  { id: 'pedagogy', label: 'Pedagogy', to: '/pedagogy', description: "The founder's teaching model — what a LEGO is, the extraction heuristics, and ZUT." },
  // adminOnly: a community builder (role editor) never sees the platform room
  // (Tom, 2026-09-25, "fix both" on job #183's findings). The router guard
  // refuses the routes themselves — see isAdminOnlyRoute below — and the
  // server refuses every admin API; hiding the tab is only the courtesy.
  { id: 'admin', label: 'Admin', to: '/admin', adminOnly: true, description: 'The platform room — labs, insights, activity, maintenance, users, recording and builds.' }
]

// ---------------------------------------------------------------------------
// SECTIONS — ordered; the FIRST section that claims the current route owns the
// sub-tab row. `owns` is also the route-coverage claim the guard test checks,
// so a route inside a section's prefix can never be a page the nav has not
// heard of.
//
// `items` are that section's destinations. An item with a `hub` block is also
// rendered as a card on that section's hub page (currently only /admin has
// one), so the row and the cards CANNOT list different pages: they are one
// list read twice.
// ---------------------------------------------------------------------------
export const SECTIONS = [
  {
    // Stock-take — the compiled reference. Its own row, but it is reached from
    // Admin, so the Admin primary tab stays lit while you are in it.
    id: 'stocktake',
    primary: 'admin',
    owns: ['/stocktake*'],
    items: [
      { label: 'Stock-take', to: '/stocktake', match: ['@StocktakeIndex'], description: 'The index of the compiled reference — what each derived page covers, and when it was compiled.' },
      { label: 'Pipeline', to: '/stocktake/pipeline', match: ['@DocsPipeline'], description: 'The phase servers, the agent endpoints, the validation gates and the database tables the code actually touches.' },
      { label: 'Glossary', to: '/stocktake/glossary', match: ['@DocsGlossary'], description: 'The shared terms, each pinned to the table that holds it or the gate that enforces it.' },
      { label: 'APML', to: '/stocktake/apml', match: ['@DocsApml'], description: 'The specification lineage, and the current-state facts it is checked against.' }
    ]
  },
  {
    // Admin — platform-wide tooling. Every entry here is a sub-tab AND a hub
    // card, except the hub itself.
    id: 'admin',
    primary: 'admin',
    hubPath: '/admin',
    // Only the prefixes no item declares. Every item's own `to` is owned
    // automatically (see ownsOf), so a new destination is ONE entry — it
    // cannot be a tab the section has never heard of, which is what /builds
    // was until it was hand-patched into four separate places.
    owns: ['/admin*'],
    items: [
      {
        label: 'Admin',
        to: '/admin',
        description: 'The platform hub — every admin surface as a card, grouped by what a change reaches.',
        isHubSelf: true // the hub page's own tab; a card pointing at itself would be a loop
      },
      {
        // Labs, not Configs (2026-09-01). The six surfaces here were never
        // configs — three of them write nothing at all.
        label: 'Labs',
        to: '/admin/labs',
        match: ['/admin/labs*', '/admin/configs*', '/admin/listening*'],
        hub: {
          badge: 'eight',
          description: 'Every lab in one place — Listening, Speaking, Voice, Pod, Script, VAD, Basket and Capture A/B — grouped by blast radius: who a change reaches, and when.',
          action: 'Open Labs',
          accent: '#a855f7',
          glow: 'rgba(168, 85, 247, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>'
        }
      },
      {
        // Procedures (Tom, 2026-10-12): the only place a job is started from the
        // dashboard, and only a registered deterministic one.
        label: 'Procedures',
        to: '/admin/procedures',
        match: ['/admin/procedures*'],
        hub: {
          badge: 'run',
          description: 'Run a registered deterministic job — choose its inputs, read the plan, run it, follow it, undo it.',
          action: 'Open Procedures',
          accent: '#ffa630',
          glow: 'rgba(255, 166, 48, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="6 4 20 12 6 20 6 4"/></svg>'
        }
      },
      {
        label: 'Insights',
        to: '/insights',
        hub: {
          badge: 'analytics',
          description: 'Cross-course insight boards — lifecycle, rate, coverage, content, and ops signals.',
          action: 'View Insights',
          accent: '#10b981',
          glow: 'rgba(16, 185, 129, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/></svg>'
        }
      },
      {
        label: 'Activity',
        to: '/jobs',
        badgeKey: 'activeCourses',
        hub: {
          badge: 'live',
          description: 'Running and recent jobs across the production pipeline — what is building right now.',
          action: 'View Activity',
          accent: '#3b82f6',
          glow: 'rgba(59, 130, 246, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>'
        }
      },
      {
        label: 'Maintenance',
        to: '/maintenance',
        badgeKey: 'auditStale',
        hub: {
          badge: 'ops',
          description: 'Housekeeping and health — audit log, archive, and platform upkeep.',
          action: 'Open Maintenance',
          accent: '#f59e0b',
          glow: 'rgba(245, 158, 11, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>'
        }
      },
      {
        label: 'Users',
        to: '/users',
        hub: {
          badge: 'access',
          description: 'Manage dashboard accounts, recorders, roles, and invite codes.',
          action: 'Manage Users',
          accent: '#14b8a6',
          glow: 'rgba(20, 184, 166, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>'
        }
      },
      {
        label: 'Voice coverage',
        to: '/admin/voice-coverage',
        hub: {
          title: 'Per-voice coverage', badge: 'clips',
          description: 'Clips held and complete rounds, in every voice. Cast and best alternatives for each course.',
          action: 'View coverage', accent: '#14b8a6', glow: 'rgba(20, 184, 166, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 20V10m8 10V4m8 16v-7"/></svg>'
        }
      },
      {
        // Human Recording (2026-09-02). Where each recordist's /r/ link comes
        // from; it was reachable by URL only.
        label: 'Recording',
        to: '/admin/recording',
        match: ['/admin/recording*'],
        hub: {
          title: 'Human Recording',
          badge: 'voices',
          description: 'Which languages we record with people instead of TTS, how far each has got, and the link to send each recordist.',
          action: 'Open Recording',
          accent: '#ec4899',
          glow: 'rgba(236, 72, 153, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>'
        }
      },
      {
        // Test builds (2026-09-04). Filed by what it does, not by who you are:
        // handing someone the Android test build is an admin job.
        label: 'Test builds',
        to: '/builds',
        hub: {
          badge: 'android',
          description: 'The current Android test build, how to install it, and where it came from.',
          action: 'Open Test builds',
          accent: '#6366f1',
          glow: 'rgba(99, 102, 241, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
        }
      },
      {
        // HUB CARD ONLY, no sub-tab (Tom, 2026-09-04: "Stock-take is weird in
        // that it appears in a nav, but then kind of has its own page"). It
        // advertised itself as a sibling of Insights and Users and then, once
        // you were inside it, took the whole row over with its own four
        // destinations. Admin is where it is genuinely entered from, so it is
        // a card there and nothing else; the stocktake section owns the row.
        // Declared, not conventional — the guard test asserts this shape.
        hubOnly: true,
        label: 'Stock-take',
        to: '/stocktake',
        match: [],
        hub: {
          badge: 'reference',
          description: 'The compiled current-state reference — pipeline, glossary and APML, regenerated from the code.',
          action: 'Open Stock-take',
          accent: '#64748b',
          glow: 'rgba(100, 116, 139, 0.15)',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>'
        }
      }
    ]
  },
  {
    // A course being created — one door, the text editor.
    id: 'course-new',
    primary: 'courses',
    owns: [],
    when: (route) => route.params?.courseCode === 'new',
    items: [{ label: 'Text', to: '/production/new/text', match: ['*'], description: 'The text surface for a course being created — the first seeds before the course exists.' }]
  },
  {
    // ONE door per course (Tom, 2026-06-10): Overview is the hub — every
    // working surface (Text, Audio, Record Room, QA, …) is a card there.
    id: 'course',
    primary: 'courses',
    owns: ['/production*'],
    when: (route) => route.path.startsWith('/production/') && !!route.params?.courseCode,
    // A COMMUNITY BUILDER NEVER GOES VIA THE OVERVIEW (Tom, 2026-09-25: "we
    // don't use course overview for community users … We go from course
    // journey and each link goes to the place they actually do stuff"). Their
    // one course door is the Journey; admins keep the Overview hub.
    items: (route, viewer = {}) => viewer.isAdmin === false
      ? [{
          label: 'Journey',
          to: `/production/${route.params.courseCode}/journey`,
          match: ['@LeaderJourney'],
          description: 'The course journey — record, proofread or build, each straight to where the work is done.'
        }]
      : [{
          label: 'Overview',
          to: `/production/${route.params.courseCode}`,
          match: ['@ProductionDashboard'],
          description: 'One course\'s hub — text, audio, recording and QA, each as a card.'
        }]
  },
  {
    // The courses library and the canonical data browsers, one row.
    id: 'courses',
    primary: 'courses',
    // The library, a course's own editor page, and the canonical browsers.
    owns: ['/courses*', '/course/*', '/canonical*'],
    items: [
      { label: 'Library', to: '/courses', description: 'Every course on the estate, with its state, opened for editing.' },
      { label: 'Seeds', to: '/canonical/seeds', match: ['@CanonicalSeeds'], description: 'The canonical seed sentences, in pedagogical order, with their {target} placeholders.' },
      { label: 'Content', to: '/canonical/content', match: ['@CanonicalContent'], description: 'The canonical content store — seeds, encouragements and the per-pair welcome messages.' },
      { label: 'Pods', to: '/canonical/pods', match: ['@PodsDoc'], description: 'The listening pods — the canonical English master and the pods generated from it.' },
      { label: 'Script Lab', to: '/canonical/scripts', match: ['@ScriptLab', '@ScriptLabScript'], description: "The canonical English master scripts — edited here, and nowhere else." },
      // The graph the scripts are walks over (Tom asked for it by name,
      // 2026-08-31); it was reachable only from a button inside the Script Lab.
      { label: 'Metagraph', to: '/canonical/metagraph', match: ['@Metagraph'], description: 'The shape the scripts are walks over — what the learner has to survive, in delivery order.' },
      // Generated from stored tags + the live seeds and pod lines (Tom, 2026-10-21).
      { label: 'Layers × shapes', to: '/canonical/layers-shapes', match: ['@LayersShapes'], description: 'Speakability layers × Q/R/S × the metagraph shapes — the empty cells, the gaps and the flows, regenerated from the live pods.' }
    ]
  },
  {
    // Pedagogy — the founder's teaching model. It was one of three pages under
    // a "How & Why" tab until Tom deprecated the other two on 2026-09-04, so
    // the survivor is the tab: one page, one primary tab, no hub in between.
    id: 'pedagogy',
    primary: 'pedagogy',
    owns: [],
    // A ROW OF ONE IS NOT A ROW. The primary tab already IS this destination,
    // so a sub-bar underneath it would only repeat itself. Declared here
    // rather than inferred from items.length, because the course sections
    // have one item each and DO want their row (it carries the crumb).
    soloTab: true,
    items: [{ label: 'Pedagogy', to: '/pedagogy', match: ['@Pedagogy'], description: "The teaching model itself — the two extraction heuristics, what a LEGO is, and why ZUT decides." }]
  }
]

// ---------------------------------------------------------------------------
// ROUTES DELIBERATELY OUTSIDE THE NAV. Every one carries the reason it is not
// a nav destination. The guard test fails on any route that is neither owned
// by a section nor listed here, so a new page cannot arrive unnoticed — and
// this list is the honest, single answer to "what can you only reach by URL?".
// Redirect-only routes and `meta.public` routes are exempt automatically:
// a redirect is not a destination, and the navbar is hidden on public pages.
// ---------------------------------------------------------------------------
export const OUTSIDE_NAV = [
  { path: '/', why: 'the Home hub itself — the brand link is its door' },
  { path: '/hq', why: 'SSi HQ moved to the learning app — a one-line note for old bookmarks' },
  { path: '/pipeline', why: 'the old pipeline board, kept reachable by URL while the nav settles' },
  { path: '/monitor', why: 'agent monitor — opened from Activity, not a top-level destination' },
  { path: '/monitor/:courseCode', why: 'agent monitor for one course — opened from Activity' },
  { path: '/network-builder', why: 'one-off builder tool, opened by URL' },
  { path: '/validate', why: 'validator entry — opened from a course surface' },
  { path: '/validate/:courseCode', why: 'validator for one course — opened from a course surface' },
  { path: '/copy', why: 'copy editor index — opened by URL by whoever is editing copy' },
  { path: '/copy/:docId', why: 'one copy doc — opened from the copy index' },
  { path: '/htw-copy', why: 'How-it-works copy editor — opened by URL' },
  { path: '/edit/introductions', why: 'introductions editor — opened from a course surface' },
  { path: '/courses/:code/progress', why: 'per-course progress — opened from the course overview' },
  { path: '/pods/scripts', why: 'pod script index — opened from the Pods browser' },
  { path: '/pods/scripts/:courseCode', why: 'pod scripts for one course — opened from the Pods browser' },
  { path: '/quality/:courseCode', why: 'quality dashboard — opened from the course overview' },
  { path: '/quality/:courseCode/seeds/:seedId', why: 'one seed review — opened from the quality dashboard' },
  { path: '/quality/:courseCode/evolution', why: 'prompt evolution — opened from the quality dashboard' },
  { path: '/quality/:courseCode/health', why: 'health report — opened from the quality dashboard' },
  { path: '/quality/:courseCode/learned-rules', why: 'learned rules — opened from the quality dashboard' },
  { path: '/recursive-upregulation', why: 'methodology explainer, linked from the Pedagogy page' },
  { path: '/audio-preview', why: 'estate audio preview entry — opened by URL' },
  { path: '/my-recording', why: 'a recordist\'s own room — booth navbar (navbarMode): the main bar, cut down for a recorder' },
  { path: '/record/:courseCode?', why: 'the Record Room shell — the navbar is hidden for recorders' },
  { path: '/:pathMatch(.*)*', why: 'the 404 catch-all' }
]

// ---------------------------------------------------------------------------
// Derivations. Every rendered nav surface goes through these.
// ---------------------------------------------------------------------------

/**
 * Everything a section claims: the prefixes it declares, PLUS every one of its
 * own destinations. Derived, so a destination can never be listed in a row
 * while its section disowns the route — the /builds defect by construction.
 */
export function ownsOf(section) {
  const items = typeof section.items === 'function' ? [] : section.items
  return [...(section.owns || []), ...items.map((i) => i.to)]
}

/** The section that owns the current route, or null. First match wins. */
export function sectionFor(route) {
  return SECTIONS.find((s) => (s.when ? s.when(route) : matchesAny(ownsOf(s), route))) || null
}

/** True when `primaryId`'s tab should be lit for this route. */
export function isPrimaryActive(primaryId, route) {
  const section = sectionFor(route)
  return !!section && section.primary === primaryId
}

/**
 * Is this route part of the Admin tab's territory? Derived from the same
 * declaration the tab is, so the lock and the nav cannot disagree: whatever
 * lights up Admin is admin-only. The one exception is the Android test build,
 * which is for everyone with a Popty login (recordists are exactly who we hand
 * test APKs to).
 */
export const ADMIN_TERRITORY_OPEN_TO_ALL = ['/builds', '/admin/procedures']
export function isAdminOnlyRoute(route) {
  if (ADMIN_TERRITORY_OPEN_TO_ALL.includes(route.path)) return false
  const section = sectionFor(route)
  return !!section && section.primary === 'admin'
}

/** viewer: { isAdmin } — omitted means an admin, which is what the guard test reads. */
export function primaryTabs(route, viewer = {}) {
  return PRIMARY_TABS.filter((t) => !(t.adminOnly && viewer.isAdmin === false)).map((t) => ({
    label: t.label,
    to: t.to,
    active: isPrimaryActive(t.id, route)
  }))
}

/**
 * The one-line description of a destination: its own `description`, or the hub
 * card copy it already carries. One place to read it from, so the explainer
 * never needs a second copy of the nav.
 */
export function descriptionOf(item) {
  return item.description ?? item.hub?.description ?? null
}

/** What lights an item up: its own declared patterns, or its destination. */
export function matchOf(item) {
  return item.match ?? [item.to]
}

function itemsOf(section, route, viewer) {
  return typeof section.items === 'function' ? section.items(route, viewer) : section.items
}

/** The sub-tab row for the current route: the owning section's items. */
export function sectionTabs(route, badges = {}, viewer = {}) {
  const section = sectionFor(route)
  if (!section) return []
  // A section that declares itself solo renders no row: its primary tab is the
  // destination, and a one-tab bar repeating it is noise.
  if (section.soloTab) return []
  // hubOnly items are doors on the hub page, not siblings in the row.
  return itemsOf(section, route, viewer).filter((item) => !item.hubOnly).map((item) => ({
    label: item.label,
    to: item.to,
    active: matchOf(item).includes('*') ? true : matchesAny(matchOf(item), route),
    badge: item.badgeKey ? badges[item.badgeKey] || null : null
  }))
}

/** The hub cards for a section: the same items, read for their `hub` block. */
export function hubCards(sectionId) {
  const section = SECTIONS.find((s) => s.id === sectionId)
  if (!section || typeof section.items === 'function') return []
  return section.items
    .filter((item) => item.hub)
    .map((item) => ({ title: item.hub.title || item.label, to: item.to, ...item.hub }))
}

/**
 * WHERE "HOME" TAKES A COMMUNITY BUILDER (Tom, 2026-09-25: "always easy to get
 * back to these three cards" — the Home link and the Popty wordmark on every
 * editor page). Home is the course journey's three cards, opened in full
 * (?cards=all) even when a pick is remembered: the course in the URL if there
 * is one, else their only course, else the Home hub (a multi-course editor
 * needs the picker first). An admin's Home stays the hub.
 * viewer: { isAdmin, courses }.
 */
export function editorHomeTo(route, viewer = {}) {
  if (viewer.isAdmin !== false) return '/'
  const courses = Array.isArray(viewer.courses) ? viewer.courses : []
  const code = route?.params?.courseCode || (courses.length === 1 ? courses[0] : null)
  return code ? { path: `/production/${code}/journey`, query: { cards: 'all' } } : '/'
}

/**
 * WHICH NAVBAR A PAGE SHOWS (job #411, Aran 2026-10-14: the booth pages "need to have the same
 * top navbar as the main Popty"). One answer for AppNavbar, so the rule is testable here:
 *   'full'    — the main Popty bar, exactly as it is filtered for this viewer's role.
 *   'minimal' — the same bar with nothing in it this viewer cannot use: the wordmark, and the
 *               account menu if they are signed in. For a booth visitor main Popty has no bar to
 *               copy — a signed-out artist on a private link (#380) or a 'recorder' login is
 *               refused every console page — so they get no tabs, no course switcher, no Deploy.
 *   'hidden'  — no bar.
 * Booth pages declare `meta.boothNav`. While the mic is live the bar goes entirely: no link
 * within reach of a thumb mid-take is the booth's own rule (RecordistRoom, the stage).
 * viewer: { isAuthenticated, isAdmin, isRecorder, micLive }.
 */
export function navbarMode(route, viewer = {}) {
  if (route?.meta?.boothNav) {
    if (viewer.micLive) return 'hidden'
    return viewer.isAuthenticated && !viewer.isRecorder ? 'full' : 'minimal'
  }
  if (route?.meta?.public === true || viewer.isRecorder) return 'hidden'
  return 'full'
}

/**
 * THE RECORDING LINK (job #411, Aran 2026-10-14): on every page the navbar shows, for a signed-in
 * login that holds at least one recording voice — and for nobody else. It goes to /my-recording,
 * the login door that opens their own booth with a tab per voice. `voices` is
 * GET /api/recording/mine's list (useMyRecordingVoices). Null means no link.
 */
export function recordingTab(route, viewer = {}) {
  if (!viewer.isAuthenticated || !Array.isArray(viewer.voices) || !viewer.voices.length) return null
  return {
    label: 'Recording',
    to: '/my-recording',
    active: route?.name === 'RecordistRoom' || route?.name === 'MyRecording'
  }
}
